import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../../../api/admin-client';

/**
 * THE CONSOLE'S DATA HOOKS.
 *
 * Two of them, and the split matters: `useAdminList` owns cursor pagination and
 * server-side filtering, `useAdminResource` owns a single payload. Between them
 * they are the only place in the console that talks to the network, so the
 * loading, empty, error and stale-request rules are written once.
 *
 * ── EVERY FILTER IS A SERVER PARAMETER ─────────────────────────────────────
 *
 * Nothing here filters or sorts in the browser. The brief asks this to hold at
 * a hundred thousand users, and a client-side filter over a fifty-row page is
 * not a filter — it is a filter over page one that quietly lies about the rest.
 *
 * ── THE RACE THIS CLOSES ───────────────────────────────────────────────────
 *
 * Typing in the search box fires a request per keystroke (debounced, but still
 * several). They can come back out of order, and the LAST response wins in the
 * naive version — which is not the same as the LATEST QUERY winning. Each run
 * carries a sequence number and a stale response is dropped, so the table
 * always shows the answer to the query that is in the box.
 */

const DEBOUNCE_MS = 250;

export interface ListState<T> {
  items: T[];
  total: number | null;
  loading: boolean;
  /** True only for a "load more", so the table dims rather than blanking. */
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
}

export function useAdminList<T>(
  resource: string,
  params: api.ListParams = {},
  { debounce = false }: { debounce?: boolean } = {}
): ListState<T> {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // The params object is rebuilt on every render by every caller, so it can
  // never be a dependency directly — it would loop. Serialising it is what
  // makes "the filters actually changed" a comparable value.
  const key = JSON.stringify(params);
  const seq = useRef(0);
  const more = useRef<string | null>(null);
  // ── WHY A REF AND NOT `loadingMore` ──────────────────────────────────────
  //
  // Infinite scroll fires `loadMore` from an IntersectionObserver, and an
  // observer can fire twice before React has re-rendered with `loadingMore`
  // true. Both calls would read the SAME `more.current` cursor and append the
  // SAME page — a list that silently duplicates rows as you scroll, which then
  // looks like a backend paging bug rather than a UI one.
  //
  // A ref is written synchronously, so the second call sees the first.
  const inFlight = useRef(false);

  const run = useCallback(
    async (nextCursor: string | null) => {
      const mine = ++seq.current;
      if (nextCursor) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const parsed = JSON.parse(key) as api.ListParams;
        const page = await api.list<T>(resource, { ...parsed, cursor: nextCursor ?? undefined });
        // A response from a query the user has already moved on from.
        if (mine !== seq.current) return;
        setItems(prev => (nextCursor ? [...prev, ...page.items] : page.items));
        setTotal(typeof page.total === 'number' ? page.total : null);
        more.current = page.next;
        setCursor(page.next);
      } catch (e) {
        if (mine !== seq.current) return;
        setError(e instanceof Error ? e.message : 'Could not load');
        if (!nextCursor) setItems([]);
      } finally {
        // Released whoever the response belongs to, not only the latest: a
        // superseded "load more" that never clears this leaves the list frozen
        // with hasMore true and nothing able to fire.
        inFlight.current = false;
        if (mine === seq.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [resource, key]
  );

  // Stable across renders on purpose. The infinite-scroll effect in AdminTable
  // lists this in its dependencies, and a fresh function every render would
  // tear down and rebuild the IntersectionObserver on every state change.
  const loadMore = useCallback(() => {
    if (inFlight.current || !more.current) return;
    inFlight.current = true;
    run(more.current);
  }, [run]);

  useEffect(() => {
    if (!debounce) {
      run(null);

      return undefined;
    }
    const t = setTimeout(() => run(null), DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [run, debounce, nonce]);

  return {
    items,
    total,
    loading,
    loadingMore,
    error,
    hasMore: !!cursor,
    loadMore,
    reload: () => setNonce(n => n + 1),
  };
}

export interface ResourceState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  reload: () => void;
}

export function useAdminResource<T>(path: string | null): ResourceState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!path) {
      setData(null);
      setLoading(false);

      return;
    }
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    setNotFound(false);
    api
      .detail<T>(path)
      .then(d => {
        if (mine !== seq.current) return;
        setData(d);
      })
      .catch(e => {
        if (mine !== seq.current) return;
        // A 404 on a DETAIL path is an ordinary miss — the record is gone, or
        // the id was mistyped. On a LIST path the client turns it into
        // AdminAccessError instead, because there a 404 means "you are not an
        // admin any more"; see the note in admin-client.ts.
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        if ((e as any)?.response?.status === 404) setNotFound(true);
        else setError(e instanceof Error ? e.message : 'Could not load');
      })
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
  }, [path, nonce]);

  return { data, loading, error, notFound, reload: () => setNonce(n => n + 1) };
}

/**
 * The step-up clock.
 *
 * Polled once a minute rather than counted down locally, and that is
 * deliberate: a local countdown drifts when the tab is backgrounded (browsers
 * throttle timers) and would tell an operator they still have four minutes
 * while the server has already expired the cookie. Asking is cheap; being wrong
 * about it costs a failed action mid-task.
 */
export function useElevation() {
  const [session, setSession] = useState<api.AdminSession | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setSession(await api.getSession());
    } catch {
      /* the shell's own error handling covers a dead session */
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);

    return () => clearInterval(t);
  }, [refresh]);

  return {
    session,
    elevated: (session?.elevated_for_s ?? 0) > 0,
    remaining: session?.elevated_for_s ?? 0,
    busy,
    /* true, or the sentence to show. A 429 (the step-up limiter) used to come
       back as a plain `false` and the dialog blamed the password — an operator
       typing the right one was told it was wrong. */
    elevate: async (password: string): Promise<true | string> => {
      setBusy(true);
      try {
        await api.elevate(password);
        await refresh();

        return true;
      } catch (e) {
        if ((e as { response?: { status?: number } })?.response?.status === 429) {
          return 'Too many confirmation attempts. Wait a few minutes and try again.';
        }

        return 'That password was not accepted. The attempt has been recorded.';
      } finally {
        setBusy(false);
      }
    },
    stepDown: async () => {
      await api.stepDown();
      await refresh();
    },
    refresh,
  };
}
